import { DatabasePercentageField } from '~/api-client/models/DatabasePercentageField';
import {
  ActivePermission,
  Address,
  Customer,
  CustomerShort,
  Database,
  DatabaseCalcField,
  DatabaseCalcFieldToken,
  DatabaseChecklistField,
  DatabaseCombinedTextField,
  DatabaseCreatedByField,
  DatabaseCreatedDateField,
  DatabaseCurrencyField,
  DatabaseDateField,
  DatabaseEntry,
  DatabaseEntryFileMetaData,
  DatabaseEntryStatisticsField,
  DatabaseField,
  DatabaseFieldType,
  DatabaseFieldVisibilityOperator,
  DatabaseFileField,
  DatabaseImageField,
  DatabaseModifiedByField,
  DatabaseModifiedDateField,
  DatabaseNumberField,
  DatabaseOwnerField,
  DatabasePeriodField,
  DatabaseTemplate,
  DatabaseTextField,
  DatabaseUser,
  DatabaseYesNoField,
  HTTPRequestInit,
  Organisation,
  Product,
  ProductCategory,
  Purchase,
  RequestOpts,
  TimeUnit,
} from '~/api-client';
import { endOfDay, intlFormat, isValid, parseISO } from 'date-fns';
import { buildPurchasableFromProducts, Purchasable } from '~/modules/common/stores/products';
import _ from 'lodash';
import { isVisible, COMPARISON_OPERATOR_MAP } from '~/modules/common/services/util/visibilityHelpers';
import { laufendeNummerText } from './laufendeNummer';
// Re-export für Backward-Compat: viele Stellen importieren COMPARISON_OPERATOR_MAP
// noch aus util.ts.
export { COMPARISON_OPERATOR_MAP };

export function dateIsoStringToDate(dateString: string) {
  if (!!dateString) {
    return new Date(dateString);
  }

  return null;
}

export function getUserDisplayNameComplete(user: Customer) {
  if (!user) {
    return '';
  }

  let name = '';

  if (!!user.company) {
    name += user.company;

    if (!!user.firstName) {
      name += ' (' + user.firstName;

      if (!!user.lastName) {
        name += ' ' + user.lastName + ')';
      }
    } else if (!!user.lastName) {
      name += ' (' + user.lastName + ')';
    }
  } else {
    name = `${user.firstName} ${user.lastName}`;
  }

  return name;
}

export function getUserDisplayName(user: Customer) {
  if (!user) {
    return '';
  }

  let name = '';

  if (!!user.firstName) {
    name += user.firstName;

    if (!!user.lastName) {
      name += ' ' + user.lastName;
    }
  } else if (!!user.lastName) {
    name += user.lastName;
  }

  return name;
}

export function getDisplayNameFromAddress(address: Address) {
  if (!address) {
    return '';
  }

  if (!!address.companyName) {
    return address.companyName;
  }

  return `${address.firstName} ${address.lastName}`;
}


export function getUserSortName(user: Customer | CustomerShort | undefined) {
  if (!user) {
    return '';
  }

  if (!!user.company) {
    return user.company.toLowerCase();
  }

  return `${user.lastName}${user.firstName}`.toLowerCase();
}

export function getValidPurchases(purchases: Array<Purchase>, type?: EditionType): Array<Purchase> {
  return purchases.filter(p => (!!type ? p.editionType === type : true));
}

export function getValidNextPurchasesAfterNow(purchases: Array<Purchase>, type?: EditionType): Array<Purchase> {
  const today = endOfDay(new Date()).valueOf();

  return purchases.filter(p => (!!type ? p.editionType === type : true) && (!!p.edition?.releaseDate && endOfDay(new Date(p.edition?.releaseDate!!)).valueOf() >= today));
}

export function adjustTimeToNoon(date: Date | undefined) {
  if (!date) {
    return date;
  }

  const result = new Date(date);
  result.setHours(12, 0, 0, 0);
  return result;
}

export function compareArray<T>(arr1: Array<T>, arr2: Array<T>, compFn?: (obj1: T, obj2: T) => boolean) {
  if (!!arr1 && !arr2) {
    return false;
  }

  if (!arr1 && !!arr2) {
    return false;
  }

  if (!arr1 && !arr2) {
    return false;
  }

  if (arr1.length != arr2.length) {
    return false;
  }

  const compareFn = !compFn ? (obj1: T, obj2: T) => obj1 === obj2 : compFn;

  for (let i = 0; i < arr1.length; i++) {
    const obj1 = arr1[i];
    const obj2 = arr2[i];

    if (!compareFn(obj1, obj2)) {
      return false;
    }
  }
  return true;
}

export function dateStringProver(dateString: String) {

  let tempDateString = dateString.replaceAll(/[&\/\\#,+()$~%.'":*?<>{}-]/g, '');

  let tempDatStringArray = tempDateString.split(/(\s+)/).filter(e => e.trim().length > 0);

  let tempStringExport = '';


  let counter1 = 0;
  let counter2 = 0;
  let counter3 = 0;

  while (counter1 < tempDatStringArray.length) {

    while (counter2 < monthNames.length) {
      if (monthNames[counter2].includes(tempDatStringArray[counter1])) {
        tempDatStringArray[counter1] = monthNames[counter2];
      }

      counter2++;
    }

    counter2 = 0;
    counter1++;
  }


  while (counter3 < tempDatStringArray.length) {

    tempStringExport = tempStringExport + tempDatStringArray[counter3];

    if (counter3 < (tempDatStringArray.length - 1)) {
      tempStringExport = tempStringExport + ' ';
    }

    counter3++;
  }

  return tempStringExport;
}

export function hasStarted(date: Date | undefined) {
  if (!date) {
    return false;
  }

  const todayDate = new Date();
  const startingDate = new Date(date);
  return todayDate > startingDate;
}

export function isExpired(date: Date | undefined) {
  if (!date) {
    return false;
  }

  const todayDate = new Date();
  const expirationDate = new Date(date);
  return todayDate > expirationDate;
}

export function getOrganisationName(orga: Organisation | undefined, type: String) {
  if (!orga) {
    return '';
  }
  if (type === 'code') return orga.code;
  if (type === 'name') return orga.name;
}

export function getSearchDate(date?: Date) {
  if (!date) {
    return '01 01 1970';
  }

  const day = ('00' + new Date(date).getDate()).slice(-2);
  const month = monthNames[(new Date(date).getMonth())];
  const year = new Date(date).getFullYear();

  return day + ' ' + month + ' ' + year;
}

export function createRequestOptionFunction(headers: HeadersInit) {
  return (requestContext: { init: HTTPRequestInit, context: RequestOpts }) => {
    let finalHeaders: HeadersInit = {};

    if (requestContext.init && requestContext.init.headers) {
      finalHeaders = { ...requestContext.init.headers };
    }

    finalHeaders = {
      ...finalHeaders,
      ...headers,
    };

    return Promise.resolve({ headers: finalHeaders });
  };
}

export function handleFileDownload(data: Blob, filename: string) {
  let fileURL = window.URL.createObjectURL(data);
  let fileLink = document.createElement('a');

  fileLink.href = fileURL;
  fileLink.setAttribute('download', filename);
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
}

export function handleFileOpenInTab(data: any) {
  let fileURL = window.URL.createObjectURL(new Blob([data]));
  window.open(fileURL, '_blank');
}

export async function buildImageUrlFromBlob(binaryData: Blob | undefined) {
  if (!binaryData) {
    return undefined;
  }

  if (process.server) {
    return undefined;// buildImageUrlFromBlobOnServerSide(binaryData);
  }

  if (!!binaryData && binaryData.size > 0) {
    return URL.createObjectURL(binaryData);
  }

  return undefined;
}

async function buildImageUrlFromBlobOnServerSide(blob: Blob) {
  let avatarUrl = undefined;
  let binaryData = Buffer.from(await blob.arrayBuffer());

  if (!!binaryData && binaryData.byteLength > 0) {
    const base64 = imageToBase64(binaryData);
    avatarUrl = `data:${blob.type};base64,` + base64;
  }

  return avatarUrl;
}

function imageToBase64(buf: Buffer) {
  return Buffer.from(buf, 'binary').toString('base64');
}

/**
 * ══ WEM GEHOERT DIESER DATENSATZ — fuer die Rechtepruefung ══════════════════
 *
 * Das Backend entscheidet ueber `owner`:
 *   Criteria.where("owner").is(customer.getId())     // eigene
 *   Criteria.where("owner").ne(customer.getId())     // fremde
 *
 * DIE OBERFLAECHE FRAGTE DAS FALSCHE FELD. An zehn Stellen stand
 * `entry.createdBy?.username` — also der AUTOR, und davon die MAILADRESSE.
 * Verglichen wird aber gegen `user.id`. Eine Mailadresse ist nie eine
 * Kunden-Kennung, der Zweig „eigener Datensatz" griff also NIE: Wer „nur
 * eigene aendern" vergeben hatte, sah an seinen EIGENEN Datensaetzen keine
 * Knoepfe. (Betreiber-Frage 10.09.2026: „Autor? Besitzer? Bearbeiter?")
 *
 * Es war kein Leck — das Backend prueft sauber. Es war eine Bedienung, die
 * weniger anbot, als der Benutzer durfte.
 *
 * DREI FELDER, EINES ZAEHLT:
 *   owner     — Besitzer, beim Anlegen gesetzt   → dafuer
 *   createdBy — Autor                            → nur Nachweis
 *   updatedBy — Bearbeiter                       → nur Nachweis
 *
 * `createdBy` bleibt als Rueckfall, aber als OBJEKT (also ueber dessen `id`):
 * Aeltere Datensaetze koennen ohne `owner` gekommen sein, und dann ist der
 * Autor die beste verfuegbare Auskunft. Fehlt beides, kommt `undefined` heraus
 * — die Pruefung faellt dann auf „eigene ODER fremde" zurueck, wie bisher.
 */
export function entryOwner(entry: any): any | undefined {
  return entry?.owner ?? entry?.createdBy ?? undefined;
}

export function userCanUpdate(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  // WICHTIG: `typeof null === 'object'` in JS -> ohne Null-Guard crasht `owner.id`
  // mit "Cannot read properties of null (reading 'id')" (User-Befund 2026-07-03:
  // angemeldeter Plattform-Nutzer auf fremder DB). owner darf null/undefined sein.
  const ownerId = owner && typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.updateOwnedEntries || user.id !== ownerId && permissions.updateForeignEntries;
  }
  if (user) {
    return permissions.updateOwnedEntries || permissions.updateForeignEntries;
  }
  return false;
}

export function userCanDelete(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  // WICHTIG: `typeof null === 'object'` in JS -> ohne Null-Guard crasht `owner.id`
  // mit "Cannot read properties of null (reading 'id')" (User-Befund 2026-07-03:
  // angemeldeter Plattform-Nutzer auf fremder DB). owner darf null/undefined sein.
  const ownerId = owner && typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.deleteOwnedEntries || user.id !== ownerId && permissions.deleteForeignEntries;
  }
  if (user) {
    return permissions.deleteOwnedEntries || permissions.deleteForeignEntries;
  }
  return false;
}

export function userCanRead(user: Customer | undefined, permissions: ActivePermission, owner?: Customer | string) {
  // WICHTIG: `typeof null === 'object'` in JS -> ohne Null-Guard crasht `owner.id`
  // mit "Cannot read properties of null (reading 'id')" (User-Befund 2026-07-03:
  // angemeldeter Plattform-Nutzer auf fremder DB). owner darf null/undefined sein.
  const ownerId = owner && typeof owner === 'object' ? owner.id : owner;
  if (user && ownerId) {
    return user.id === ownerId && permissions.readOwnedEntries || user.id !== ownerId && permissions.readForeignEntries;
  }
  if (user) {
    return permissions.readOwnedEntries || permissions.readForeignEntries;
  }
  return false;
}

export function userCanAdd(user: Customer, permissions: ActivePermission) {
  return permissions.create;
}

export function userIsDbOwner(customer: Customer | null | undefined, database: Database) {
  // Null-Guard: ein angemeldeter Nicht-Member hat für eine fremde DB u.U. keinen
  // Customer im Kontext -> ohne Guard crasht `customer.id` mit
  // "Cannot read properties of null (reading 'id')" (User-Befund 2026-07-03).
  const ownerId = database.owner?.id;
  return !!customer && !!ownerId && customer.id === ownerId;
}

export const calculateUnitDayTime = (unitAmount: number, unitTime: TimeUnit) => {
  switch (unitTime) {
    case 'DAY':
      return unitAmount;
    case 'WEEK':
      return unitAmount * 7;
    case 'MONTH':
      return unitAmount * 30;
    case 'YEAR':
      return unitAmount * 365;
  }
};

export function initializeProductsUtil(result: Product[]): Array<any> {

  if (!result) {
    throw new Error('unable to load products');
  }
  // build purchasable arrays by category

  const productsByCategory: { [category: string]: Product[] } = {};
  const productById: { [id: string]: Product } = {};
  const categoryById: { [id: string]: ProductCategory } = {};

  result.forEach((key: Product) => {
    const category = key.productCategory!;

    let products = productsByCategory[category.valueOf()];

    if (!products) {
      products = [];
      productsByCategory[category.valueOf()] = products;
    }
    categoryById[key.id!] = key.productCategory!;
    productsByCategory[category.valueOf()].push(key);
    productById[key.id!] = key;
  });

  const purchasablesByCategory: { [category: string]: Purchasable[] } = {};

  Object.keys(productsByCategory).forEach(category => {
    purchasablesByCategory[category] = buildPurchasableFromProducts(productsByCategory[category]);
  });

  const purchasableById: { [id: string]: Purchasable } = {};

  Object.keys(purchasablesByCategory).forEach(category => {
    purchasablesByCategory[category].forEach(purchasable => {
      purchasableById[purchasable.id] = purchasable;
    });
  });

  const categories = Object.keys(purchasablesByCategory);

  return [productsByCategory, productById, purchasablesByCategory, purchasableById, categories, categoryById];
}

export const getDaysDiff = (start: Date, end: Date) => {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  return Math.round(Math.abs((end.getTime() - start.getTime()) / oneDay));
};

export enum SetDateUnit {DAYS = 'DAYS', MONTHS = 'MONTHS'}

export const dateForRow = (date: Date, dateUnit?: SetDateUnit, monthsToAdd: number = 0, daysToAdd: number = 0, asDate: boolean = false) => {
  const { $i18n } = useNuxtApp();
  let dateToReturn;
  if (!!date) {
    if (!!dateUnit) {
      switch (dateUnit) {
        case SetDateUnit.DAYS:
          dateToReturn = new Date(_.cloneDeep(date).setDate(date.getDate() + daysToAdd));
          break;
        case SetDateUnit.MONTHS:
          dateToReturn = new Date(_.cloneDeep(date).setMonth(date.getMonth() + monthsToAdd));
          break;
      }
    } else {
      dateToReturn = new Date(date);
    }
    if (asDate) {
      return dateToReturn;
    }
    return $i18n.d(dateToReturn, 'short');

  }
  return ' - ';
};

export function errorMsg(error: any, defaultMessage?: string): Promise<string> {
  if (error.response?.body) {
    const reader = error.response.body.getReader();

    return reader.read().then((status: any) => {
      const error: string = new TextDecoder().decode(status.value);
      return error;
    });
  }

  return Promise.resolve(defaultMessage ?? 'Es ist ein unbekannter Fehler aufgetreten.');
}

export type GroupedEntries = { [key: string]: DatabaseEntry[] };

export const parseGroupByFieldId = (groupByFieldId: string | undefined): { fieldId: string; variant?: string } => {
  if (!groupByFieldId) return { fieldId: '' };
  const idx = groupByFieldId.lastIndexOf(':');
  if (idx !== -1) {
    return { fieldId: groupByFieldId.substring(0, idx), variant: groupByFieldId.substring(idx + 1) };
  }
  return { fieldId: groupByFieldId };
};

export const groupBy = (database: Database, data: DatabaseEntry[]) => {
  const groupByFieldId = database.settings!.groupBy;
  const variant = (database.settings as any).groupByVariant as string | undefined;
  const field = database.fields?.find(f => f.id === groupByFieldId)!;
  return groupByField(field, data, variant, database);
};

const CATEGORY_FIELD_TYPES_SET = new Set([
  'CATEGORY_FIELD', 'CATEGORY_FIELD_SELECT_LOOK', 'CATEGORY_FIELD_TOGGLE_LOOK', 'CATEGORY_FIELD_RADIO_LOOK',
]);

/**
 * Feldarten, deren Wert ein DATUM ist — als Zeichenkette, nicht als Zahl.
 *
 * ══ WARUM DAS EINE EIGENE MENGE BRAUCHT (Befund 30.09.2026) ═══════════════
 *
 * `JAHR(Datum)` lieferte fuer den 30.09.2026 das Ergebnis **1970**.
 *
 * Grund: In der Formel wird ein Feld zu `getOrDefault('id', 0)`, und dieser
 * Helfer gibt NUR Zahlen heraus:
 *
 *     (typeof context[val] === 'number') ? context[val] : def
 *
 * Ein Datum steht im Kontext als `"2026-09-30T00:00:00.000Z"` — keine Zahl,
 * also kam die Vorgabe `0` zurueck. `new Date(0)` ist der 1.1.1970.
 *
 * Betroffen war JEDE Datumsfunktion: JAHR, MONAT, TAG und DAUER_IN_TAGEN.
 * Letztere rechnete stillschweigend immer 0 Tage.
 */
const DATE_FIELD_TYPES_SET = new Set([
  'DATE_FIELD', 'CREATED_DATE_FIELD', 'MODIFIED_DATE_FIELD',
]);

/**
 * Die Beschriftungen der gewaehlten Kategorien — EINZELN.
 *
 * Herausgeloest aus `resolveCategoryVariant`, weil es seit dem 28.08.2026 zwei
 * Leser gibt: Der eine verbindet sie zu einem Text (die bisherige, verbundene
 * Gruppe), der andere braucht sie getrennt (ein Eintrag je gewaehlter
 * Kategorie). Zweimal dieselbe Aufloesung zu schreiben hiesse, dass die eine
 * beim naechsten neuen Feldtyp anders auffasst als die andere.
 */
const resolveCategoryLabels = (field: DatabaseField, storedValue: string, variant?: string): string[] => {
  const catField = field as any;
  const values = Array.isArray(storedValue) ? storedValue : [storedValue];
  return values.map((v: string) => {
    const opt = catField.content?.find((o: any) => o.value === v);
    if (!opt) return v;
    switch (variant) {
      case 'wert': return opt.wert ?? opt.label ?? opt.value ?? v;
      case 'icon': return opt.icon ?? opt.label ?? opt.value ?? v;
      default: return opt.label ?? opt.value ?? v;
    }
  });
};

const resolveCategoryVariant = (field: DatabaseField, storedValue: string, variant?: string): string =>
  resolveCategoryLabels(field, storedValue, variant).join(', ');

export const groupByField = (field: DatabaseField, data: DatabaseEntry[], variant?: string, database?: Database | DatabaseTemplate) => {
  const isCategoryField = CATEGORY_FIELD_TYPES_SET.has(field.fieldType);
  const displayFn = isCategoryField ? null : getDisplayValueFn(field, database);

  /*
   * MEHRFACHAUSWAHL TRENNEN (Vorgabe des Betreibers 28.08.2026).
   *
   * Ohne den Schalter werden die gewaehlten Kategorien zu einem Text
   * verbunden: „Webkonto, Kreditkarte" wird eine eigene, dritte Gruppe neben
   * „Webkonto" und „Kreditkarte". Mit dem Schalter taucht der Eintrag unter
   * JEDER gewaehlten Kategorie auf, und die zusammengesetzte Gruppe entsteht
   * gar nicht erst.
   *
   * DIE EINSTELLUNG WIRD HIER GELESEN, nicht von den vier Ansichten
   * hereingereicht: Alle vier reichen `database` ohnehin schon durch, und ein
   * fuenfter Parameter waere vier Mal zu pflegen — beim naechsten Aufrufer
   * einmal vergessen, und die Liste gruppierte anders als die Galerie.
   */
  const trenneMehrfach = isCategoryField
    && (database as any)?.settings?.groupBySplitMulti === true;

  const unordered = data.reduce((groups, currentEntry) => {
    const value = currentEntry.fieldsValues?.[field.id!] ?? '';
    /* Ein Eintrag kann in mehreren Gruppen stehen — deshalb eine Liste von
       Schluesseln, auch wenn es meistens genau einer ist. */
    const keys = isCategoryField
      ? (trenneMehrfach
        ? resolveCategoryLabels(field, value, variant)
        : [resolveCategoryVariant(field, value, variant)])
      : [String(displayFn!(value, currentEntry))];
    for (const key of keys) {
      if (!groups[key]) groups[key] = [];
      groups[key].push(currentEntry);
    }
    return groups;
  }, {} as GroupedEntries);

  if (!isCategoryField) return unordered;

  // Re-order groups to match the order defined in the category field options
  const catField = field as any;
  const optionLabels: string[] = (catField.content ?? []).map((opt: any) => {
    switch (variant) {
      case 'wert': return opt.wert ?? opt.label ?? opt.value ?? '';
      case 'icon': return opt.icon ?? opt.label ?? opt.value ?? '';
      default: return opt.label ?? opt.value ?? '';
    }
  });
  const ordered: GroupedEntries = {};
  for (const label of optionLabels) {
    if (label in unordered) ordered[label] = unordered[label];
  }
  // Append groups not matching any known option (e.g. empty value, legacy data)
  for (const key of Object.keys(unordered)) {
    if (!(key in ordered)) ordered[key] = unordered[key];
  }
  return ordered;
};

// TODO move to useFormatter
export const formattedNumber = (
  value: number | bigint | undefined, precision = 0, maxPrecision = precision,
  /**
   * Tausenderpunkt setzen? Standard ja — nur eine JAHRESZAHL braucht keinen.
   * Siehe `istDatumsteil` und den Befund des Betreibers vom 14.10.2026.
   */
  mitTausenderpunkt = true,
) => {
  return typeof value === 'number' ? new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: maxPrecision,
    minimumFractionDigits: precision,
    useGrouping: mitTausenderpunkt,
  }).format(value) : '';
};

/**
 * Rechnet diese Formel einen DATUMSTEIL aus — Jahr, Monat oder Tag?
 *
 * ══ DER BEFUND (Betreiber 14.10.2026) ════════════════════════════════════
 *
 * „Ich habe hier einen Jahrgang mittels berechnetem Feld ermittelt. Dieser
 * darf keinen Tausender-Punkt haben." Sein Feld „Berechnetes Jahr" rechnet
 * `JAHR(Datum)` und stand als `2.026` da.
 *
 * ENTSCHIEDEN WIRD AN DER FORMEL, NICHT AM WERT. Zu raten, dass eine Zahl
 * zwischen 1900 und 2100 ein Jahr sei, waere genau die inhaltsgekoppelte
 * Logik, die wir uns verboten haben — und sie laege bei einem Betrag von
 * 2.026 EUR daneben. Die Rechenvorschrift dagegen sagt es eindeutig: Wer
 * `JAHR(`, `MONAT(` oder `TAG(` rechnet, bekommt eine Datumsangabe heraus,
 * und die traegt keinen Tausenderpunkt.
 *
 * MONAT und TAG stehen mit dabei, obwohl sie nie dreistellig werden. Die
 * Regel heisst „Datumsteile sind keine Mengen" — mit nur dem Jahr waere sie
 * eine Einzelfallausnahme, die beim naechsten Mal wieder jemand sucht.
 */
export const istDatumsteil = (formel: DatabaseCalcFieldToken[] | undefined): boolean =>
  (formel ?? []).some((t) => t.type === 'OPERATOR'
    && ['JAHR(', 'MONAT(', 'TAG('].includes(String(t.value)));

// TODO move to useFormatter
/**
 * `anzeige`: „code" (EUR) ist die Vorgabe der Anwendung. „symbol" (€) wählt
 * eine Vorlage, deren Gäste ein Zeichen erwarten — die Speisekarte
 * (Betreiber 22.10.2026: „3,00 €").
 */
export const formattedCurrency = (value: number | bigint | undefined, currency: string, precision = 2,
                                  anzeige: 'code' | 'symbol' = 'code') => {
  return typeof value === 'number' ? new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    style: 'currency',
    currency: currency ?? '',
    currencyDisplay: anzeige,
  }).format(value) : '';
};

// TODO move to useFormatter
export const formattedPercentage = (value: number | bigint | undefined, precision = 0) => {
  return typeof value === 'number' ? new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
    style: 'percent',
  }).format(value) : '';
};

type UserFields = 'username' | 'firstName' | 'lastName';
const formatUserField = (user: DatabaseUser, field: UserFields, useBrackets: boolean): string => {
  const value = user[field];
  if (!value) return '';
  return useBrackets && field === 'username' ? `(${value})` : value;
};

/**
 * Welche Angaben ein Benutzerfeld zeigt, wenn keine angekreuzt ist.
 *
 * ══ DER BEFUND (Betreiber 15.10.2026) ════════════════════════════════════
 *
 * „Im Ansichtsmodus werden Besitzer und Bearbeiter nicht angezeigt."
 * GEMESSEN an seiner „Dokumentenbibliothek II" auf dev: Die drei Felder
 * stehen in der Maske — „bearbeiter:", „bearbeiter von:", „Besitzer:" —, und
 * hinter jedem Doppelpunkt NICHTS. Gespeichert tragen alle drei weder
 * `ownerFields` noch `userFields`.
 *
 * Der Umschalter in der Feldeinstellung hat die Vorgabe `['username']`, gibt
 * sie aber erst beim ANZEIGEN des Umschalters ab (`onMounted`). Wer das Feld
 * anlegt, ohne diesen Teil der Maske aufzuklappen, speichert eine leere
 * Liste — und bekommt ein Feld, das nie etwas zeigt. Nichts meldet sich; es
 * steht nur eine Beschriftung ohne Wert da.
 *
 * DIE VORGABE GEHOERT IN DIE ANZEIGE, nicht nur in die Maske: Eine leere
 * Liste heisst „nichts gewaehlt", nicht „nichts zeigen". Dieselbe Vorgabe
 * wie im Umschalter, damit Einstellung und Anzeige dasselbe sagen.
 */
export const VORGABE_BENUTZERANGABEN: UserFields[] = ['username'];

/**
 * Die Anzeige eines Benutzerfeldes.
 *
 * `quelle` sagt, WESSEN Name gemeint ist. Bis zum 15.10.2026 stand hier fuer
 * alle drei Feldarten `entry.updatedBy`: Besitzer und Ersteller zeigten in
 * Listen und zusammengesetzten Texten den ZULETZT AENDERNDEN. Das faellt nur
 * dort nicht auf, wo beide dieselbe Person sind — und genau dort wird es
 * auch nicht geprueft. Die Maske machte es laengst richtig (`useUserData`
 * mit `watchProp`); nur dieser zweite Weg nicht.
 */
const createUserDisplayFunction = (
  fields: UserFields[],
  quelle: 'owner' | 'createdBy' | 'updatedBy' = 'updatedBy',
) => {
  if (!fields.length) fields = [...VORGABE_BENUTZERANGABEN];
  return (value: any, entry: DatabaseEntry | undefined): string => {
    const dbUser = (entry?.[quelle] as DatabaseUser | undefined) ?? value as DatabaseUser | undefined;
    if (!dbUser) return '';
    const useBrackets = fields.includes('username') && fields.length > 1;
    const orderedFields: UserFields[] = ['firstName', 'lastName', 'username'];
    return orderedFields
      .filter(f => fields.includes(f) && dbUser[f])
      .map(field => formatUserField(dbUser, field, useBrackets))
      .join(' ');
  };
};

const evaluateAsJavascript = (expression: string) => {
  const getOrDefault = (val: any, def: any) => {
    return def;
  };
  // Create a function that evaluates the expression in a controlled environment
  const evaluate = new Function('getOrDefault', `try {
        return ${expression};
    } catch (e) {
        return {error: e.message};
    }`);
  return evaluate(getOrDefault);
};

// ── Formatiertes (TipTap-)HTML fuer den zusammengesetzten Text inlinen ────────
// EINE Quelle der Wahrheit fuer ALLE Render-Pfade (getDisplayValueFn UND die
// DISTRIBUTED-Ansicht in DbCombinedTextField). Frueher gab es zwei Kopien — eine
// wurde gefixt (oeffnendes <p> entfernen, 1d4a41b5), die andere blieb kaputt und
// zerhackte den verteilten Text. Daher hier zentralisiert.
const COMBINED_HEADING_SIZES: Record<string, string> = { h1: '2em', h2: '1.5em', h3: '1.17em', h4: '1em', h5: '0.83em' };
const COMBINED_BLOCK_TAG_RE = /<\/?(div|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th)(\s[^>]*)?\s*\/?>/gi;

/** Headings -> gestylte Inline-Spans (Groesse + fett bleiben erhalten). */
export const convertHeadings = (html: string): string =>
  html
    .replace(/<(h[1-5])(\s[^>]*)?>/gi, (_, tag) => `<span style="font-size:${COMBINED_HEADING_SIZES[tag.toLowerCase()]};font-weight:bold">`)
    .replace(/<\/h[1-5]>/gi, '</span>');

/** Formatiertes HTML zu EINER Zeile inlinen: Block-Tags raus, </p>/<br> -> Leerzeichen. */
export const htmlToInline = (html: string): string =>
  convertHeadings(html)
    .replace(/<\/p>/gi, ' ')
    .replace(/<p(\s[^>]*)?>/gi, '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(COMBINED_BLOCK_TAG_RE, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Wie htmlToInline, aber </p>/<br> -> Zeilenumbruch (fuer die vertikale Ansicht). */
export const htmlToLines = (html: string): string =>
  convertHeadings(html)
    .replace(/<\/p>/gi, '\n')
    .replace(/<p(\s[^>]*)?>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(COMBINED_BLOCK_TAG_RE, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n+/g, '\n')
    .trim();

/**
 * @param roh Bei `true` gibt die Funktion fuer ein BERECHNUNGSFELD die nackte
 *   Zahl zurueck statt der formatierten Zeichenkette.
 *
 *   WOFUER (Betreiber 14.10.2026): Die Auswertung las bisher nur den
 *   GESPEICHERTEN Wert eines Berechnungsfeldes. Der entsteht aber erst beim
 *   Speichern eines Datensatzes — ein neu angelegtes Rechenfeld hat auf allen
 *   vorhandenen Datensaetzen keinen. In der Maske stand die Zahl (dort wird
 *   live gerechnet), in Kreuztabelle und Diagramm blieb die Zeile leer.
 *   Sein Beleg aus den eigenen Daten: „Endbestand" hatte Werte in 1 von 65
 *   Datensaetzen.
 *
 *   ZURUECKRECHNEN AUS DER ANZEIGE GEHT NICHT: Sie formatiert als Waehrung
 *   oder als Prozent, und Prozent multipliziert mit hundert — aus der
 *   Zeichenkette liesse sich der Wert nicht verlaesslich zurueckgewinnen.
 */
export const getDisplayValueFn = (fieldDef: DatabaseField, database?: Database | DatabaseTemplate, roh = false): ((value: any, entry: DatabaseEntry | undefined, additionalData?: any) => string | number) => {
  switch (fieldDef.fieldType) {

    case DatabaseFieldType.CalcField: {
      const fd = fieldDef as DatabaseCalcField;

      // Map display labels stored by DbFormulaEditor to valid JavaScript
      const toJsOp = (v: string): string => ({
        '−': '-', '×': '*', '÷': '/',
        '≥': '>=', '≤': '<=', '=': '===', '≠': '!==',
        // Logische Verknuepfungen aus den Editor-Buttons „ODER"/„UND"
        // (DbFormulaEditor). Ohne diese Abbildung blieb z.B. „ODER" als
        // nackter Bezeichner im generierten JS stehen -> SyntaxError ->
        // die Formel fiel still auf 0 zurueck (Fragebogen-Feld „Ergebnis").
        'ODER': '||', 'UND': '&&',
        'WENN(':     '(',
        ') DANN (': ') ? (',
        ') SONST (': ') : (',
        'DAUER_IN_TAGEN(': '__daysDiff(',
        'ZEITRAUM(': '__periodDiff(',
        'JAHR(':   '__year(',
        'MONAT(':  '__month(',
        'TAG(':    '__day(',
        ';':            ',',
        'Tagesdatum':   '__today',
        'Akt. Monat':   '__currentMonth',
        'Akt. Jahr':    '__currentYear',
      } as Record<string, string>)[v] ?? v;

      const stringifyTokens: any = (tokens: DatabaseCalcFieldToken[]) => tokens.map((token) => {
        if (token.type === 'FIELD') {
          const f = database?.fields?.find((f) => f.id === token.value);
          if (f?.fieldType === DatabaseFieldType.PercentageField) {
            return `(+getOrDefault('${token.value}', 0) / 100)`;
          }
          if (f?.fieldType === DatabaseFieldType.CategoryField) {
            // Kategoriefeld in einer Formel: NICHT den gespeicherten Antwort-
            // Wert (= value/Text) summieren, sondern den numerischen `wert`
            // der gewaehlten Option (z.B. Punkte 1/0). __catVal loest value ->
            // wert ueber die eingebackene __CATMAP auf; Mehrfachauswahl = Summe.
            // Fallback: numerischer value (Legacy-Kategorien ohne wert).
            return `__catVal('${token.value}')`;
          }
          if (f?.fieldType === DatabaseFieldType.PeriodField) {
            return `(context['${token.value}'] || '')`;
          }
          /* Datum als ZEICHENKETTE durchreichen — siehe DATE_FIELD_TYPES_SET.
             Ueber `getOrDefault` kaeme 0 heraus und damit das Jahr 1970. */
          if (f?.fieldType && DATE_FIELD_TYPES_SET.has(f.fieldType)) {
            return `(context['${token.value}'] || '')`;
          }
          if (f?.fieldType === DatabaseFieldType.CalcField) {
            /*
             * ══ IN KLAMMERN, SONST RECHNET DIE FORMEL FALSCH ═════════════
             *
             * Zeigt eine Formel auf ein ANDERES Berechnungsfeld, wird dessen
             * Formel hier eingesetzt. Ohne Klammern verliert sie dabei ihren
             * Zusammenhalt:
             *
             *   Ruecklage − Monatsergebnis      (Monatsergebnis = A − B)
             *   wurde zu
             *   Ruecklage − A − B        statt   Ruecklage − (A − B)
             *
             * An den Zahlen des Betreibers (14.10.2026): 861,46 − (4232,60 −
             * 3899,24) = 528,10. Ohne Klammern kam −7.270,38 heraus. Es
             * stuerzt nichts ab und es meldet sich nichts — die Zahl ist
             * einfach falsch, und zwar umso falscher, je groesser die
             * Summanden sind.
             *
             * DASSELBE GILT FUER MAL UND GETEILT: `2 × Monatsergebnis` waere
             * `2 × A − B` geworden.
             */
            return `(${(stringifyTokens((f as DatabaseCalcField).formula ?? []) as string[]).join(' ')})`;
          }
          return `getOrDefault('${token.value}', 0)`;
        } else if (token.type === 'CONSTANT') {
          const v = token.value ?? '';
          if (v !== '' && !isNaN(Number(v))) return v;
          return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
        } else {
          return toJsOp(token.value ?? '');
        }
      });

      // value -> wert Abbildung je Kategoriefeld (nur Optionen mit numerischem
      // `wert`). Wird in die evaluationFunction eingebacken (__CATMAP), damit
      // Kategorie-Bezuege in der Formel ihren Punkte-/Zahlenwert liefern.
      const catWertMap: Record<string, Record<string, number>> = {};
      (database?.fields ?? []).forEach((cf) => {
        if (!CATEGORY_FIELD_TYPES_SET.has(cf.fieldType) || !cf.id) return;
        const m: Record<string, number> = {};
        (((cf as any).content) ?? []).forEach((o: any) => {
          const wRaw = o?.wert;
          if (wRaw !== undefined && wRaw !== null && String(wRaw).trim() !== '' && !isNaN(Number(wRaw))) {
            m[String(o?.value ?? o?.wert ?? o?.label ?? '')] = Number(wRaw);
          }
        });
        catWertMap[cf.id] = m;
      });

      // Enthaelt die Formel eine Kennzahl ueber die zugeordneten Datensaetze
      // einer Unterdatenbank? Dann rechnet ausschliesslich der Server.
      const hatKennzahl = (fd.formula ?? []).some(t => t.type === 'AGGREGATE');

      /*
       * ══ „LEER LASSEN, WENN EIN WERT FEHLT" (Betreiber 30.09.2026) ═════════
       *
       * Ohne diese Einstellung faellt ein leeres Bezugsfeld auf 0 zurueck. Die
       * Rechnung liefert dann eine Zahl, die wie ein Ergebnis AUSSIEHT, aber
       * keines ist — eine Summe ohne den halben Beitrag, ein Mittelwert ueber
       * die Haelfte der Werte. Nichts stuerzt ab, nichts meldet sich.
       *
       * EINE 0 IST EIN WERT, kein fehlender: Wer 0 eintraegt, hat etwas
       * gesagt. Leer sind nur `null`, `undefined` und die leere Zeichenkette
       * — bei Mehrfachauswahl zusaetzlich das leere Feld.
       *
       * Kennzahlen (AGGREGATE) zaehlen NICHT mit: Sie haengen an fremden
       * Datensaetzen, die der Browser gar nicht kennt; ihr Wert kommt fertig
       * vom Server.
       *
       * ══ JEDES FELD ENTSCHEIDET FÜR SICH — LEERE VERERBT SICH ═════════════
       *
       * Berichtigt am 30.09.2026 nach einem Befund des Betreibers: Ein
       * Rechenfeld blieb leer, „obwohl beide Werte für die Formel vorhanden
       * sind".
       *
       * Die erste Fassung sah DURCH verschachtelte Rechenfelder hindurch bis
       * auf die Eingabefelder ganz unten. Nachgestellt auf DEV:
       *
       *     A = 100, B = leer,  C = A + B  → C zeigt 100
       *     D = 30,             E = C − D  → E blieb LEER
       *
       * Beide Operanden von E zeigen eine Zahl, trotzdem galt E als
       * unvollstaendig. Das kann niemand nachvollziehen: Der Bildschirm sagt
       * das eine, die Regel das andere.
       *
       * Der Gedanke dahinter war, dass ein Rechenfeld nie leer ist und der
       * Schalter deshalb bei Formeln ueber Rechenfeldern nie greifen wuerde.
       * Das stimmt so nicht — ein Rechenfeld IST leer, wenn sein EIGENER
       * Schalter greift. Genau daran haengt es jetzt:
       *
       *   • Eingabefeld ohne Wert            → fehlt
       *   • Rechenfeld ohne eigenen Schalter → rechnet immer etwas, fehlt nie
       *   • Rechenfeld MIT eigenem Schalter  → fehlt, wenn ihm selbst etwas
       *                                        fehlt (und so weiter nach unten)
       *
       * Damit ist der tiefe Fall weiterhin erreichbar — man schaltet ihn bei
       * C ebenfalls ein, dann wird C leer und E folgt. Nur passiert es nicht
       * mehr hinter dem Ruecken dessen, der auf den Bildschirm sieht.
       */
      const istLeer = (w: unknown): boolean =>
        w === null || w === undefined || w === ''
        || (Array.isArray(w) && w.length === 0);

      /** Die Felder, auf die eine Formel UNMITTELBAR verweist. */
      const direkteBezuege = (tokens: DatabaseCalcFieldToken[] | undefined): string[] =>
        (tokens ?? []).filter((tk) => tk.type === 'FIELD' && !!tk.value)
          .map((tk) => tk.value as string);

      /*
       * `gesehen` bricht Kreise ab. Zwei Rechenfelder, die sich gegenseitig
       * verwenden, sind zwar unsinnig, aber anlegbar — ohne diese Sperre
       * liefe die Pruefung in einen endlosen Abstieg und die Maske bliebe
       * stehen.
       */
      const feldFehlt = (id: string, werte: Record<string, unknown>,
                         gesehen: Set<string>): boolean => {
        if (gesehen.has(id)) return false;
        const f = database?.fields?.find((x) => x.id === id);
        if (f?.fieldType === DatabaseFieldType.CalcField) {
          if ((f as any).hideWhenIncomplete !== true) return false;
          const naechste = new Set(gesehen).add(id);
          return direkteBezuege((f as DatabaseCalcField).formula)
            .some((ref) => feldFehlt(ref, werte, naechste));
        }
        return istLeer(werte?.[id]);
      };

      const unvollstaendig = (werte: Record<string, unknown>): boolean =>
        (fd as any).hideWhenIncomplete === true
        && direkteBezuege(fd.formula).some((id) => feldFehlt(id, werte, new Set([fd.id])));

      /*
       * ══ „LEER LASSEN, WENN KEIN EINZIGES BEZUGSFELD GEFUELLT IST" ════════
       *
       * Vorgabe des Betreibers 13.10.2026, gemessen an seiner Depot-Wand:
       * Der „Gesamtwert" summiert die Wertfelder ALLER Rubriken, ein
       * Datensatz traegt aber nur die seiner eigenen. Bei 53 von 69
       * Eintraegen stand deshalb „0,00 EUR" — und das behauptet etwas
       * anderes als „nichts eingetragen".
       *
       * `hideWhenIncomplete` hilft dort nicht: Es verlangt, dass ALLE
       * Bezuege gefuellt sind; bei einander ausschliessenden Feldern bliebe
       * das Feld dann immer leer. Dieser Schalter verlangt nur EINEN.
       *
       * ALLE BEZUEGE, auch die eines Kind-Rechenfeldes ohne eigenen
       * Schalter: Die Frage ist nicht „darf gerechnet werden", sondern
       * „steht ueberhaupt irgendwo eine Zahl".
       *
       * Dieselbe Regel steckt im Server in `CalculationParser`
       * (`alleBezuege`). Laufen die beiden auseinander, zeigt die Liste
       * etwas anderes als die Maske.
       */
      const alleBezuege = (feld: DatabaseCalcField, gesehen: Set<string>): string[] => {
        if (feld.id && gesehen.has(feld.id)) return [];
        const naechste = new Set(gesehen);
        if (feld.id) naechste.add(feld.id);
        const raus: string[] = [];
        for (const id of direkteBezuege(feld.formula)) {
          const f = database?.fields?.find((x) => x.id === id);
          if (f?.fieldType === DatabaseFieldType.CalcField) {
            raus.push(...alleBezuege(f as DatabaseCalcField, naechste));
          } else {
            raus.push(id);
          }
        }
        return raus;
      };

      const alleLeer = (werte: Record<string, unknown>): boolean => {
        if ((fd as any).hideWhenAllEmpty !== true) return false;
        const bezuege = alleBezuege(fd, new Set());
        return bezuege.length > 0 && bezuege.every((id) => istLeer(werte?.[id]));
      };

      const formulaParts = fd.formula ? stringifyTokens(fd.formula).join(' ') : '0';
      const unclosedBrackets = [...formulaParts].reduce((n, c) => n + (c === '(' ? 1 : c === ')' ? -1 : 0), 0);
      const formula = unclosedBrackets > 0 ? formulaParts + ')'.repeat(unclosedBrackets) : formulaParts;

      const DATE_HELPERS = `
        const __today = Date.now();
        const __currentMonth = new Date().getMonth() + 1;
        const __currentYear = new Date().getFullYear();
        const __year  = (d) => { try { return new Date(d).getFullYear(); }        catch(e) { return 0; } };
        const __month = (d) => { try { return new Date(d).getMonth() + 1; }       catch(e) { return 0; } };
        const __day   = (d) => { try { return new Date(d).getDate(); }             catch(e) { return 0; } };
        const __daysDiff = (a, b) => { try { return Math.round((new Date(b) - new Date(a)) / 86400000); } catch(e) { return 0; } };
        const __periodDiff = (p) => { try { if (!p || typeof p !== 'string') return 0; const parts = p.split('#'); const s = parts[0]; const e = parts[1]; if (!s || !e) return 0; if (!isNaN(Number(s)) && !isNaN(Number(e)) && s.length <= 4 && e.length <= 4) return Number(e) - Number(s); const ds = new Date(s); const de = new Date(e); if (isNaN(ds.getTime()) || isNaN(de.getTime())) return 0; const diff = de.getTime() - ds.getTime(); const bothMidnight = ds.getUTCHours()===0 && ds.getUTCMinutes()===0 && ds.getUTCSeconds()===0 && de.getUTCHours()===0 && de.getUTCMinutes()===0 && de.getUTCSeconds()===0; return bothMidnight ? diff / 86400000 : diff / 1000; } catch(ex) { return 0; } };
      `;

      let evaluationFunction: Function = () => 0;
      try {
        evaluationFunction = new Function('context', `
          const getOrDefault = (val, def, forceNumber = false) => (typeof context[val] === 'number' || forceNumber) ? context[val] ?? def : def;
          const __CATMAP = ${JSON.stringify(catWertMap)};
          const __catVal = (fid) => {
            const m = __CATMAP[fid] || {};
            const one = (v) => {
              if (v === null || v === undefined || v === '') return 0;
              const w = m[String(v)];
              if (w !== undefined) return w;          // numerischer wert der Option
              const n = Number(v);                    // Fallback: numerischer value (Legacy)
              return isNaN(n) ? 0 : n;
            };
            const raw = context[fid];
            return Array.isArray(raw) ? raw.reduce((s, v) => s + one(v), 0) : one(raw);
          };
          ${DATE_HELPERS}
          try {
            const result = ${formula};
            // Text-Ergebnis (WENN/DANN mit Text-Konstanten, z.B.
            // „Bestanden"/„Nicht bestanden") unveraendert durchreichen —
            // nur echte numerische NaN-Faelle auf 0 normalisieren.
            if (typeof result === 'string') { return result; }
            if (isNaN(result)) { return 0; }
            return result;
          } catch(e) { return 0; }
        `);
      } catch(e) {
        // SyntaxError in formula — evaluationFunction stays as () => 0
      }

      return (value: any, entry: DatabaseEntry | undefined) => {
        /* Fehlt ein Bezugswert, wird NICHTS gezeigt — auch kein 0. Siehe
           `unvollstaendig`. Steht vor allem anderen, damit auch ein vom
           Server mitgelieferter Wert nicht doch erscheint. */
        if (unvollstaendig(entry?.fieldsValues ?? {})) return '';
        /* Summe aus nichts ist nichts — siehe `alleLeer`. */
        if (alleLeer(entry?.fieldsValues ?? {})) return '';
        // Kennzahl aus einer Unterdatenbank: NIE selbst nachrechnen.
        // Der Browser kennt die zugeordneten Kind-Datensaetze nicht — die
        // Formel wuerde hier auf 0 fallen. Der Server liefert den fertigen
        // Wert mit; er kann auch Text sein („erster Wert" eines Textfelds),
        // deshalb reicht die Zahlen-Pruefung unten nicht aus.
        if (hatKennzahl) {
          if (value === null || value === undefined || value === '') return '';
          if (typeof value === 'string' && isNaN(Number(value))) return value;
        }
        const raw = (value !== null && value !== undefined && !isNaN(Number(value)))
          ? Number(value)
          : (hatKennzahl ? '' : evaluationFunction(entry?.fieldsValues ?? {}));
        // Text-Ergebnis (aus WENN/DANN) unformatiert ausgeben — so kann ein
        // CALC-Feld auch bedingten Text liefern, nicht nur Zahlen.
        if (typeof raw === 'string') return raw;
        const num = raw;
        /* Die nackte Zahl — fuer die Auswertung, die damit rechnet. */
        if (roh) return num;
        if (['EUR', 'USD', 'GBP'].includes(fd.suffix ?? '')) {
          return formattedCurrency(num, fd.suffix ?? '', fd.precision);
        }
        if (['PERCENT'].includes(fd.suffix ?? '')) {
          return formattedPercentage(num, fd.precision ?? 0);
        }
        /* Eine Jahreszahl traegt keinen Tausenderpunkt — siehe `istDatumsteil`. */
        return formattedNumber(num, fd.precision ?? 0, fd.precision ?? 0,
          !istDatumsteil(fd.formula));
      };
    }

    case DatabaseFieldType.CategoryField: {
      return (value: any) => Array.isArray(value) ? value.join(', ') : value;
    }
    case DatabaseFieldType.CombinedTextField: {
      const fd = fieldDef as DatabaseCombinedTextField;
      const separator = (fd as any).displayDirection === 'VERTICAL' ? '\n' : ' ';

      /*
       * ══ JEDER TEIL WEISS, OB ER ANSCHLIESST (Betreiber 04.10.2026) ══════
       *
       * „Ohne Leerzeichen." Bis dahin setzte der zusammengesetzte Text
       * zwischen je zwei Teilen ein Leerzeichen; bei Satzzeichen wurde aus
       * „Polarnacht (Hanna Kvist)" ein „Polarnacht ( Hanna Kvist )".
       *
       * Deshalb liefert jeder Teil jetzt nicht mehr nur seinen Text,
       * sondern ein Paar aus Text und der Frage, ob vor ihm getrennt wird.
       * Zusammengefuegt wird erst danach — vorher ist nicht entscheidbar,
       * welcher Teil der erste ist, weil leere und unsichtbare Teile
       * herausfallen.
       *
       * NICHT AUS DEM INHALT GERATEN: Eine Regel „vor einer Klammer kein
       * Leerzeichen" waere an den Text gekoppelt und braeche beim naechsten
       * Zeichen, das jemand benutzt. Der Gestalter sagt es am Teil.
       */
      const alsText = (w: unknown) => String(w ?? '')
        .replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, '\\n');
      const stringifyTokens: any = (tokens: DatabaseCalcFieldToken[]) => tokens.map((token) => {
        const eng = (token as any).ohneLeerzeichen === true ? 'true' : 'false';
        if (token.type === 'FIELD') {
          const f = database?.fields?.find((f) => f.id === token.value);
          if (f?.fieldType === DatabaseFieldType.CombinedTextField) {
            /* Ein Verweis auf ein anderes zusammengesetztes Feld setzt
               dessen Formel ein. Traegt der VERWEIS den Schalter, gilt er
               fuer dessen ersten Teil — sonst ginge er unterwegs verloren. */
            return `engAnfang(visible('${f.id}') ? [${(stringifyTokens((f as DatabaseCombinedTextField).formula ?? []) as string[]).join(', ')}] : false, ${eng})`;
          }
          return `{ v: visible('${token.value}') && getOrDefault('${token.value}', ''), eng: ${eng} }`;
        } else {
          /* MASKIERT: Ein Hochkomma im Text — „Rock'n'Roll" — hat die
             erzeugte Funktion bis hierher zerrissen. */
          return `{ v: '${alsText(token.value)}', eng: ${eng} }`;
        }
      });

      /*
       * ══ NICHTS ANZEIGEN, SOLANGE ETWAS FEHLT ════════════════════════════
       *
       * Vorgabe des Betreibers 02.10.2026: „Beim zusammengesetzten Feld muss
       * die Funktion aus dem Feldtyp Berechnung nachgezogen werden."
       *
       * Ohne das laesst dieses Feld leere Teile stillschweigend weg: Aus
       * „Album" und „Interpret" wird bei fehlendem Album allein der
       * Interpret — und das sieht aus wie ein Album.
       *
       * NUR DIE UNMITTELBAREN BEZUEGE. Verweist die Formel auf ein anderes
       * zusammengesetztes Feld, entscheidet DIESES fuer sich; sein Wert
       * kommt dann schon leer an, und die Leere vererbt sich von selbst.
       */
      const unvollstaendigKombi = (werte: Record<string, unknown>): boolean => {
        if ((fd as any).hideWhenIncomplete !== true) return false;
        return (fd.formula ?? [])
          .filter((tk) => tk.type === 'FIELD' && !!tk.value)
          .some((tk) => {
            const w = werte?.[tk.value as string];
            return w === null || w === undefined || String(w).trim() === '';
          });
      };

      const formula = fd.formula ? stringifyTokens(fd.formula).join(',') : '0';
      const joinSeparator = separator === '\n' ? '\\n' : ' ';
      const evaluationFunction = new Function('context', `
        const visible = (f) => context.isVisible(context.database.fields.find((field) => field.id === f), context.database, context.entry);
        const getOrDefault = (val, def) => {
          const v = context[val];
          if (typeof v === 'string') return v;
          if (typeof v === 'number') return String(v);
          return def;
        };
        const engAnfang = (teile, eng) => {
          if (!eng || !Array.isArray(teile) || !teile.length) return teile;
          return [{ ...teile[0], eng: true }, ...teile.slice(1)];
        };
        const teile = [${formula}].flat().filter((x) => x && typeof x === 'object'
          && x.v !== '' && x.v !== null && x.v !== undefined && x.v !== false);
        return teile.map((x, i) => (i > 0 && !x.eng ? '${joinSeparator}' : '') + x.v).join('');
      `);

      // Pre-process formatted text field values: convert HTML to plain text
      // so that block elements like <p> don't cause unwanted line breaks
      const formattedFieldIds = new Set(
        (database?.fields ?? [])
          .filter(f => f.fieldType === DatabaseFieldType.TextField && (f as DatabaseTextField).formatted)
          .map(f => f.id!)
      );

      // Pre-process currency field values: format with decimal places + currency symbol
      const currencySymbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
      const currencyFieldMap = new Map<string, string>();
      (database?.fields ?? [])
        .filter(f => f.fieldType === DatabaseFieldType.CurrencyField)
        .forEach(f => {
          const currency = (f as any).type ?? 'EUR';
          currencyFieldMap.set(f.id!, currencySymbols[currency] ?? currency);
        });
      /*
       * ══ DIE LAUFENDE NUMMER (Befund 31.08.2026) ═════════════════════════
       *
       * „Im Auswahlfeld steht immer noch nicht die komplette laufende Nr."
       *
       * Gestern bekam `getDisplayValueFn` einen eigenen Fall fuer den Feldtyp —
       * das half ueberall, wo die Nummer FUER SICH steht. Der zusammengesetzte
       * Text geht aber einen anderen Weg: Er bereitet die Werte seiner Felder
       * SELBST auf, je Typ (formatierter Text, Waehrung, Kategorie). Was dort
       * nicht steht, faellt als Rohwert durch — aus „TK-0001" wurde „1".
       *
       * Genau die Doppelspurigkeit, vor der der Kommentar weiter oben schon
       * warnt: zwei Render-Pfade, und ein Fix im einen heilt den anderen nicht.
       */
      const sequenceFieldMap = new Map<string, any>();
      (database?.fields ?? [])
        .filter(f => f.fieldType === DatabaseFieldType.SequenceField)
        .forEach(f => sequenceFieldMap.set(f.id!, f));
      /*
       * ══ EIN RECHENFELD STEUERT SEINEN WERT BEI, NICHT SEINE FORMEL ══════
       *
       * Befund des Betreibers 13.10.2026: Er wollte „Nr." + „/" +
       * „Gesamtwert" zusammensetzen — „eine Variante ohne Programmierung,
       * genauso ist die Anwendung gedacht."
       *
       * GEMESSEN kam heraus: gespeichert „L 5095470 00792 / ( + + + + )",
       * auf der Oberflaeche „… / 30000". Der Server kopierte die FORMEL des
       * Rechenfeldes in den Text (siehe CombinedTextParser), die Oberflaeche
       * reichte die nackte Zahl durch.
       *
       * Der Wert steht laengst bereit: Die Mongo-Kette rechnet Rechenfelder
       * VOR den zusammengesetzten Feldern. Er wird hier nur noch so
       * formatiert, wie das Rechenfeld selbst ihn zeigt — sonst stuende
       * „30000" neben „30.000,00 EUR" derselben Zahl.
       *
       * EIN TEXTERGEBNIS (aus WENN/DANN) bleibt unangetastet.
       */
      const calcFieldMap = new Map<string, DatabaseCalcField>();
      (database?.fields ?? [])
        .filter(f => f.fieldType === DatabaseFieldType.CalcField)
        .forEach(f => calcFieldMap.set(f.id!, f as DatabaseCalcField));

      // Pre-process category fields: render as colored pills
      const categoryFieldMap = new Map<string, any>();
      (database?.fields ?? [])
        .filter(f => CATEGORY_FIELD_TYPES_SET.has(f.fieldType))
        .forEach(f => categoryFieldMap.set(f.id!, f));
      // Inline-Helfer (htmlToInline/htmlToLines/convertHeadings) sind jetzt modul-
      // weit zentralisiert (eine Quelle der Wahrheit, auch fuer die DISTRIBUTED-Ansicht).
      return (value: any, entry: DatabaseEntry | undefined) => {
        /* VOR ALLEM ANDEREN: Fehlt ein Teil und ist der Schalter an, gibt es
           nichts zu bauen. Erst zu rechnen und dann wegzuwerfen wäre nur
           teurer, nicht richtiger. */
        if (unvollstaendigKombi(entry?.fieldsValues ?? {})) return '';

        const processedValues: Record<string, any> = { ...entry?.fieldsValues };
        if (formattedFieldIds.size > 0) {
          for (const id of formattedFieldIds) {
            const raw = processedValues[id];
            if (typeof raw === 'string' && raw) {
              processedValues[id] = separator === '\n' ? htmlToLines(raw) : htmlToInline(raw);
            }
          }
        }
        // Format currency fields: "1.234,56 €"
        for (const [id, symbol] of currencyFieldMap) {
          const raw = processedValues[id];
          if (typeof raw === 'number') {
            processedValues[id] = raw.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + symbol;
          }
        }
        // Rechenfelder wie das Rechenfeld selbst formatieren
        for (const [id, cf] of calcFieldMap) {
          const raw = processedValues[id];
          if (raw === null || raw === undefined || raw === '') continue;
          if (typeof raw === 'string' && isNaN(Number(raw))) continue;   // Textergebnis
          const num = Number(raw);
          if (isNaN(num)) continue;
          /*
           * AUCH HIER die Jahreszahl ohne Tausenderpunkt. Am 14.10.2026 war
           * nur der Zweig des Rechenfeldes selbst berichtigt — der
           * zusammengesetzte Text formatiert seine Bezuege SELBST und blieb
           * deshalb bei „2.026". Gemeldet hat es der Betreiber ueber die
           * Gruppierung seiner Liste, die ihr Band aus genau diesem Feld
           * beschriftet („Jahr" = zusammengesetzter Text auf „Berechnetes
           * Jahr"). Dieselbe Doppelspurigkeit, vor der die Kommentare
           * darueber warnen — ein Fix im einen Pfad heilt den anderen nicht.
           */
          processedValues[id] = ['EUR', 'USD', 'GBP'].includes(cf.suffix ?? '')
            ? formattedCurrency(num, cf.suffix ?? '', cf.precision)
            : (cf.suffix === 'PERCENT'
              ? formattedPercentage(num, cf.precision ?? 0)
              : formattedNumber(num, cf.precision ?? 0, cf.precision ?? 0,
                !istDatumsteil(cf.formula)));
        }

        // Format category fields: render as colored pills when color is set
        for (const [id, seqField] of sequenceFieldMap) {
          const roh = processedValues[id];
          if (roh === null || roh === undefined || roh === '') continue;
          processedValues[id] = laufendeNummerText(roh, seqField);
        }

        for (const [id, catField] of categoryFieldMap) {
          const raw = processedValues[id];
          if (raw == null) continue;
          const values = Array.isArray(raw) ? raw : [raw];
          processedValues[id] = values.map((v: string) => {
            const opt = catField.content?.find((o: any) => o.value === v);
            const label = opt?.label ?? opt?.value ?? v;
            if (opt?.color) {
              const textColor = opt.textColor ? `color:${opt.textColor};` : 'color:#fff;';
              return `<span style="background-color:${opt.color};${textColor}border:1px solid ${opt.color};border-radius:4px;padding:0 9px;font-size:1rem;height:auto;line-height:1.4;display:inline-block;vertical-align:middle;white-space:nowrap;box-sizing:border-box">${label}</span>`;
            }
            return label;
          }).join(' ');
        }
        return evaluationFunction({ isVisible: isVisible, database: database, entry: entry, ...processedValues });
      };
    }
    case DatabaseFieldType.SequenceField: {
      /* Ohne diesen Fall fiel die laufende Nummer in den Standardzweig und kam
         als nackte Zahl heraus — „1" statt „TK-0001". Betroffen war jeder Ort
         ausser dem Feld selbst. */
      const seq = fieldDef as any;
      return (value: any) => laufendeNummerText(value, seq);
    }
    case DatabaseFieldType.CreatedByField: {
      const fields = ((fieldDef as DatabaseCreatedByField).userFields?.filter(f => f !== 'avatar') ?? []) as UserFields[];
      return createUserDisplayFunction(fields, 'createdBy');
    }
    case DatabaseFieldType.OwnerField: {
      const fd = fieldDef as DatabaseOwnerField;
      const fields = (fd.ownerFields?.filter((f) => f !== 'avatar') ?? []) as ('username' | 'firstName' | 'lastName')[];
      return createUserDisplayFunction(fields, 'owner');
    }
    case DatabaseFieldType.CreatedDateField: {
      const fd = fieldDef as DatabaseCreatedDateField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const timestamp = entry?.created ?? value;
        if (!timestamp || !isValid(timestamp)) {
          return '';
        }
        if (fd.appearance === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
        } else if (!fd.appearance || fd.appearance === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else if (fd.appearance === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
    }
    case DatabaseFieldType.CurrencyField: {
      const fd = fieldDef as DatabaseCurrencyField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedCurrency(value, fd.type ?? '');
    }
    case DatabaseFieldType.PeriodField:
    case DatabaseFieldType.DateField: {
      const fd = fieldDef.fieldType == DatabaseFieldType.DateField ? fieldDef as DatabaseDateField : fieldDef as DatabasePeriodField;
      const getDisplayValue = (value: string) => {
        if (!value) {
          return '';
        }
        if (['PERIOD_FIELD_ONLY_YEAR_LOOK', 'PERIOD_FIELD_PLAIN_STRING_LOOK'].includes(fd.type!)) {
          return value;
        }
        const timestamp = parseISO(value);
        if (!isValid(timestamp)) {
          return '';
        }
        if (fd.type === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
          // Fehlender Subtyp = Datum MIT Uhrzeit. Vorher fiel ein Datumsfeld
          // ohne `type` in den letzten else-Zweig und lieferte einen LEEREN
          // String: Der Wert stand in der Datenbank, war aber nirgends zu
          // sehen — „das Datum wird gar nicht gesetzt" (Befund 2026-08-15 am
          // Feld „Bestätigt am" des Mailverteilers). Das
          // CREATED_DATE_FIELD oben macht es seit jeher so; ein Datumsfeld
          // darf einen gespeicherten Wert nie stillschweigend verschlucken.
        } else if (!fd.type || fd.type === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
            second: fd.withSeconds ? 'numeric' : undefined,
          }, { locale: 'de-DE' });
        } else if (fd.type === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
            second: fd.withSeconds ? 'numeric' : undefined,
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
      return (value: any, entry: DatabaseEntry | undefined) => {
        if (fd.fieldType === DatabaseFieldType.DateField) {
          return getDisplayValue(value as string);
        }
        if (fd.fieldType === DatabaseFieldType.PeriodField) {
          const values = value?.split('#');
          return values?.map(getDisplayValue).join(' - ');
        }
      };
    }
    case DatabaseFieldType.EmailField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.LinkField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.ModifiedByField: {
      const fields = ((fieldDef as DatabaseModifiedByField).userFields?.filter(f => f !== 'avatar') ?? []) as UserFields[];
      return createUserDisplayFunction(fields, 'updatedBy');
    }
    case DatabaseFieldType.ModifiedDateField: {
      const fd = fieldDef as DatabaseModifiedDateField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const timestamp = entry?.updated ?? value;
        if (!timestamp || !isValid(timestamp)) {
          return '';
        }
        if (fd.appearance === 'DATE_FIELD_ONLY_DATE_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }, { locale: 'de-DE' });
        } else if (!fd.appearance || fd.appearance === 'DATE_FIELD_DATE_AND_TIME_LOOK') {
          return intlFormat(timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else if (fd.appearance === 'DATE_FIELD_ONLY_TIME_LOOK') {
          return intlFormat(timestamp, {
            hour: 'numeric',
            minute: 'numeric',
          }, { locale: 'de-DE' });
        } else {
          return '';
        }
      };
    }
    case DatabaseFieldType.NumberField: {
      const fd = fieldDef as DatabaseNumberField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedNumber(value, fd.precision);
    }
    case DatabaseFieldType.PasswordField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.PercentageField: {
      const fd = fieldDef as DatabasePercentageField;
      return (value: any, entry: DatabaseEntry | undefined) => formattedPercentage(value, 0);
    }
    case DatabaseFieldType.RatingField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.AgileField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.TelnumberField: {
      return (value: any, entry: DatabaseEntry | undefined) => value as string;
    }
    case DatabaseFieldType.ChecklistField: {
      const fd = fieldDef as DatabaseChecklistField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        const checked = value?.filter((e) => e.checked).length ?? 0;
        const overall = value?.length ?? 0;
        return `${checked}/${overall}`;
      };
    }
    case DatabaseFieldType.YesNoField: {
      const fd = fieldDef as DatabaseYesNoField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        if (!!value && fd.activeText) {
          return fd.activeText;
        }
        if (!!!value && fd.inactiveText) {
          return fd.inactiveText;
        }
        return value;
      };
    }
    case DatabaseFieldType.ImageField: {
      const fd = fieldDef as DatabaseImageField;
      return (value: DatabaseEntryFileMetaData, entry: DatabaseEntry | undefined, additionaData?: any) => {
        const baseUrl = additionaData?.baseUrl;
        if (value) {
          return `${baseUrl}/db-media/${database!.id}/${entry?.id}/${fd.id}/${value.resourceIdentifier}`;
        }
        return '';
      };
    }
    case DatabaseFieldType.FileField: {
      const fd = fieldDef as DatabaseFileField;
      return (value: DatabaseEntryFileMetaData, entry: DatabaseEntry | undefined, additionaData?: any) => {
        const baseUrl = additionaData?.baseUrl;
        return `${baseUrl}/db-media/${database!.id}/${entry?.id}/${fd.id}/${value.resourceIdentifier}`;
      };
    }
    case DatabaseFieldType.StatisticsField: {
      const fd = fieldDef as DatabaseEntryStatisticsField;
      return (value: any, entry: DatabaseEntry | undefined) => {
        return entry?.statistics?.viewCount ?? 0;
      };
    }
    default: {
      return (value: any) => value as string;
    }
  }
};


export const getTitleSuffix = (fieldDef: DatabaseField): ((entry: DatabaseEntry | undefined, view: boolean) => string | number | null) => {
  switch (fieldDef.fieldType) {
    case DatabaseFieldType.ChecklistField: {
      const fd = fieldDef as DatabaseChecklistField;
      return (entry: DatabaseEntry | undefined, view: boolean) => {
        const value = entry?.fieldsValues?.[fieldDef.id!] ?? [];
        const checked = value?.filter((v: any) => !!v.checked).length ?? 0;
        const overall = value?.length ?? 0;
        return `(${checked}/${overall})`;
      };
    }
    default: {
      return (value: any) => '';
    }
  }
};

export const getFieldName = (field: DatabaseField, entry: DatabaseEntry, db: Database, view: boolean, titleAsFallback = false) => {
  const title = field.hideTitle || db.hideFieldTitles ? '' : (field.showTitleAsPhrase ? field.titlePhrase ?? (titleAsFallback ? field.name : '') : field.name);
  return [title, getTitleSuffix(field)(entry, view)].filter((x) => !!x).join(' ');
};

export const isContactField = (field: DatabaseField) => ['OWNER_FIELD', 'CREATED_BY_FIELD', 'MODIFIED_BY_FIELD'].includes(field.fieldType);

// Converts Java class name format (e.g. 'DatabaseImageField') to field type ID (e.g. 'IMAGE_FIELD').
// Needed because some backend endpoints return the Java class name instead of the OpenAPI discriminator value.
export const normalizeFieldType = (fieldType: string): string => {
  if (!fieldType) return fieldType;
  if (Object.values(DatabaseFieldType).includes(fieldType as DatabaseFieldType)) return fieldType;
  const found = Object.entries(DatabaseFieldType).find(([cls]) => `Database${cls}` === fieldType);
  return found ? found[1] : fieldType;
};

export const sortConditionsFromSortObject = (obj: {
  [key: string]: string
}) => !!obj ? Object.entries(obj).filter(([field, value]) => !!value).map(([field, value]) => `${field}:${value}`) : undefined;

export const filterConditionsFromFilterObject = (obj: {
  [key: string]: string | string[]
}) => !!obj ? Object.entries(obj)
  .filter(([field, value]) => value !== null && value !== undefined && value !== '' && (Array.isArray(value) ? value.length > 0 : true))
  .map(([field, value]) => `${field}:${Array.isArray(value) ? value.filter((val) => !!val).join('|') : value}`) : undefined;

interface TempUserHolder {
  user: CustomerShort,
  organisationCode?: string | undefined
}

export const assignOrganisation = (currentUser: Ref<TempUserHolder>, row: CustomerShort, assignOrganisationDialogVisible: Ref<boolean>) => {
  currentUser.value = {
    user: row,
    organisationCode: !!row.organisation ? row.organisation.code : undefined,
  };
  assignOrganisationDialogVisible.value = true;
};

export const findOrganisationName = (customer: CustomerShort) => {
  if (!!customer.organisation) {
    return customer.organisation.name;
  }
  return `-`;
};

export const deepCopyObject = (obj: any) => {
  let rv;

  switch (typeof obj) {
    case 'object':
      if (obj === null) {
        // null => null
        rv = null;
      } else {
        switch (toString.call(obj)) {
          case '[object Array]':
            // It's an array, create a new array with
            // deep copies of the entries
            rv = obj.map(deepCopyObject);
            break;
          case '[object Date]':
            // Clone the date
            rv = new Date(obj);
            break;
          case '[object RegExp]':
            // Clone the RegExp
            rv = new RegExp(obj);
            break;
          // ...probably a few others
          default:
            // Some other kind of object, deep-copy its
            // properties into a new object
            rv = Object.keys(obj).reduce(function(prev, key) {
              prev[key] = deepCopyObject(obj[key]);
              return prev;
            }, {});
            break;
        }
      }
      break;
    default:
      // It's a primitive, copy via assignment
      rv = obj;
      break;
  }
  return rv;
};

export function toRawDeep<T>(observed: T): T {
  const val = toRaw(observed);

  if (Array.isArray(val)) {
    return val.map(toRawDeep) as T;
  }

  if (val === null) return null as T;

  if (typeof val === 'object') {
    const entries = Object.entries(val).map(([key, val]) => [key, toRawDeep(val)]);

    return Object.fromEntries(entries);
  }

  return val;
}
